import { createRoot } from "react-dom/client";
import { prepareDataConnector } from "@canva/intents/data";
import type { data as canvaData } from "@canva/intents";
import { auth } from "@canva/user";
import "@canva/app-ui-kit/styles.css";
import { api } from "../api/client";
import { SelectionRoot } from "./selection-ui";

type SourceKind =
  | "monthly_metrics"
  | "income_statement"
  | "cash_flow"
  | "revenue_breakdown"
  | "expense_breakdown"
  | "headcount_breakdown";

const KNOWN_SOURCES: SourceKind[] = [
  "monthly_metrics",
  "income_statement",
  "cash_flow",
  "revenue_breakdown",
  "expense_breakdown",
  "headcount_breakdown",
];

/**
 * DataSourceRef.source payload shape — we encode org + scenario + the chosen
 * data shape so getDataTable can recover the exact selection.
 */
interface SourcePayload {
  orgId: string;
  scenarioId: string;
  source: SourceKind;
  months: number; // used by monthly_metrics
  month: string; // YYYY-MM, used by *_breakdown
}

function parseSource(raw: string): SourcePayload | null {
  try {
    const parsed = JSON.parse(raw) as Partial<SourcePayload>;
    const candidate = parsed.source as SourceKind | undefined;
    const source: SourceKind =
      candidate && KNOWN_SOURCES.includes(candidate) ? candidate : "monthly_metrics";
    if (
      typeof parsed.orgId === "string" &&
      typeof parsed.scenarioId === "string"
    ) {
      return {
        orgId: parsed.orgId,
        scenarioId: parsed.scenarioId,
        source,
        months: typeof parsed.months === "number" ? parsed.months : 12,
        month: typeof parsed.month === "string" ? parsed.month : currentMonth(),
      };
    }
  } catch {
    // fall through
  }
  return null;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Fresh Profitual token for every Canva-initiated call. */
async function authAsOrg(orgId: string): Promise<void> {
  const canvaToken = await auth.getCanvaUserToken();
  const session = await api.selectOrg(canvaToken, orgId);
  api.setToken(session.token);
}

prepareDataConnector({
  renderSelectionUi: (request: canvaData.RenderSelectionUiRequest) => {
    let container = document.getElementById("root");
    if (!container) {
      container = document.createElement("div");
      container.id = "root";
      document.body.appendChild(container);
    }
    const root = createRoot(container);
    root.render(<SelectionRoot request={request} />);
  },

  getDataTable: async (
    request: canvaData.GetDataTableRequest
  ): Promise<canvaData.GetDataTableResponse> => {
    const payload = parseSource(request.dataSourceRef.source);
    if (!payload) {
      return {
        status: "app_error",
        message: "Invalid data source reference. Please re-select the data.",
      };
    }

    try {
      if (request.signal.aborted) {
        return { status: "app_error", message: "Cancelled" };
      }

      await authAsOrg(payload.orgId);

      if (request.signal.aborted) {
        return { status: "app_error", message: "Cancelled" };
      }

      switch (payload.source) {
        case "monthly_metrics":
          return await fetchMonthlyMetrics(payload, request.limit);
        case "income_statement":
          return await fetchIncomeStatement(payload, request.limit);
        case "cash_flow":
          return await fetchCashFlow(payload, request.limit);
        case "revenue_breakdown":
        case "expense_breakdown":
        case "headcount_breakdown":
          return await fetchBreakdown(payload, request.limit);
      }
    } catch (err) {
      return {
        status: "app_error",
        message: err instanceof Error ? err.message : "Failed to fetch data",
      };
    }
  },
});

async function fetchMonthlyMetrics(
  payload: SourcePayload,
  limit: { row: number; column: number }
): Promise<canvaData.GetDataTableResponse> {
  const res = await api.getDataTable(payload.scenarioId, payload.months);

  const COLUMNS: canvaData.ColumnConfig[] = [
    { name: "Month", type: "date" },
    { name: "Revenue", type: "number" },
    { name: "Expenses", type: "number" },
    { name: "Net Income", type: "number" },
    { name: "MRR", type: "number" },
    { name: "ARR", type: "number" },
    { name: "Cash Balance", type: "number" },
    { name: "Monthly Burn", type: "number" },
    { name: "Headcount", type: "number" },
  ];

  const cappedColumns = COLUMNS.slice(0, limit.column);
  const rows = res.rows.slice(-limit.row);

  return {
    status: "completed",
    dataTable: {
      columnConfigs: cappedColumns,
      rows: rows.map((r) => ({
        cells: ([
          { type: "date", value: Math.floor(new Date(r.month).getTime() / 1000) },
          { type: "number", value: r.revenue },
          { type: "number", value: r.expenses },
          { type: "number", value: r.netIncome },
          { type: "number", value: r.mrr },
          { type: "number", value: r.arr },
          { type: "number", value: r.cashBalance },
          { type: "number", value: r.monthlyBurn },
          { type: "number", value: r.headcount },
        ] as canvaData.DataTableCell[]).slice(0, cappedColumns.length),
      })),
    },
  };
}

async function fetchBreakdown(
  payload: SourcePayload,
  limit: { row: number; column: number }
): Promise<canvaData.GetDataTableResponse> {
  const { breakdownType, labelColumn, valueColumn } = breakdownLabels(payload.source);
  const res = await api.getBreakdown(payload.scenarioId, breakdownType, payload.month);

  const COLUMNS: canvaData.ColumnConfig[] = [
    { name: labelColumn, type: "string" },
    { name: valueColumn, type: "number" },
    { name: "% of Total", type: "number" },
  ];

  const cappedColumns = COLUMNS.slice(0, limit.column);
  const rows = res.rows.slice(0, limit.row);

  return {
    status: "completed",
    dataTable: {
      columnConfigs: cappedColumns,
      rows: rows.map((r) => ({
        cells: ([
          { type: "string", value: r.name },
          { type: "number", value: r.value },
          { type: "number", value: r.percentOfTotal },
        ] as canvaData.DataTableCell[]).slice(0, cappedColumns.length),
      })),
    },
  };
}

function breakdownLabels(source: SourceKind) {
  if (source === "revenue_breakdown") {
    return { breakdownType: "revenue" as const, labelColumn: "Revenue Stream", valueColumn: "Revenue" };
  }
  if (source === "expense_breakdown") {
    return { breakdownType: "expense" as const, labelColumn: "Expense Category", valueColumn: "Expense" };
  }
  return { breakdownType: "headcount" as const, labelColumn: "Department", valueColumn: "Headcount" };
}

async function fetchIncomeStatement(
  payload: SourcePayload,
  limit: { row: number; column: number }
): Promise<canvaData.GetDataTableResponse> {
  const res = await api.getStatement(payload.scenarioId, "income", payload.months);

  const COLUMNS: canvaData.ColumnConfig[] = [
    { name: "Month", type: "date" },
    { name: "Revenue", type: "number" },
    { name: "COGS", type: "number" },
    { name: "Gross Profit", type: "number" },
    { name: "Operating Expenses", type: "number" },
    { name: "Net Income", type: "number" },
  ];

  const cappedColumns = COLUMNS.slice(0, limit.column);
  // Type narrowing: this path always receives income-shaped rows.
  const rows = (res.rows as Array<{
    month: string;
    revenue: number;
    cogs: number;
    grossProfit: number;
    operatingExpenses: number;
    netIncome: number;
  }>).slice(-limit.row);

  return {
    status: "completed",
    dataTable: {
      columnConfigs: cappedColumns,
      rows: rows.map((r) => ({
        cells: ([
          { type: "date", value: Math.floor(new Date(r.month).getTime() / 1000) },
          { type: "number", value: r.revenue },
          { type: "number", value: r.cogs },
          { type: "number", value: r.grossProfit },
          { type: "number", value: r.operatingExpenses },
          { type: "number", value: r.netIncome },
        ] as canvaData.DataTableCell[]).slice(0, cappedColumns.length),
      })),
    },
  };
}

async function fetchCashFlow(
  payload: SourcePayload,
  limit: { row: number; column: number }
): Promise<canvaData.GetDataTableResponse> {
  const res = await api.getStatement(payload.scenarioId, "cashflow", payload.months);

  const COLUMNS: canvaData.ColumnConfig[] = [
    { name: "Month", type: "date" },
    { name: "Beginning Cash", type: "number" },
    { name: "Operating", type: "number" },
    { name: "Investing", type: "number" },
    { name: "Financing", type: "number" },
    { name: "Net Change", type: "number" },
    { name: "Ending Cash", type: "number" },
  ];

  const cappedColumns = COLUMNS.slice(0, limit.column);
  const rows = (res.rows as Array<{
    month: string;
    beginningCash: number;
    operating: number;
    investing: number;
    financing: number;
    netChange: number;
    endingCash: number;
  }>).slice(-limit.row);

  return {
    status: "completed",
    dataTable: {
      columnConfigs: cappedColumns,
      rows: rows.map((r) => ({
        cells: ([
          { type: "date", value: Math.floor(new Date(r.month).getTime() / 1000) },
          { type: "number", value: r.beginningCash },
          { type: "number", value: r.operating },
          { type: "number", value: r.investing },
          { type: "number", value: r.financing },
          { type: "number", value: r.netChange },
          { type: "number", value: r.endingCash },
        ] as canvaData.DataTableCell[]).slice(0, cappedColumns.length),
      })),
    },
  };
}
